import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/atoms/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/atoms/radio-group';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  annualizedBillingOptionRevenue,
  MEMBERSHIP_BILLING_OPTION_FREQUENCY_LABELS,
  type MembershipBillingOption,
  type MembershipPlan,
} from './support/membership-plan-shared';
import type { PlanRecurringService } from '@/hooks/membership-plans/usePlanRecurringServices';
import type { MembershipSaleEquipment } from '@/lib/membership/home-sale-pricing';
import {
  membershipBillingPeriodLabel,
  priceMembershipOptionForEquipment,
} from '@/lib/membership/home-sale-pricing';
import {
  EXTRA_UNIT_PRICE_UNIT_SUFFIX,
  extraUnitPriceOf,
  type MembershipExtraUnitPrice,
} from '@/lib/membership/extra-unit-price';
import { EquipmentDescriptionCombobox } from '@/components/organisms/equipment/EquipmentDescriptionCombobox';
import { useCreateEquipment } from '@/hooks/equipment/useEquipment';
import type { EquipmentDescription } from '@/types/equipment-description';
import { plannedMembershipVisits } from '@/components/organisms/memberships/MembershipSalePromiseSummary';
import {
  COMPLIMENTARY_OPTION_LABEL,
  isComplimentaryBillingOption,
} from '@/lib/membership/complimentary-option';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: MembershipPlan | null;
  options: MembershipBillingOption[];
  services: PlanRecurringService[];
  /** Off unless the document being written can carry the answer. */
  allowDeliveredVisits?: boolean;
  servicesLoading?: boolean;
  servicesError?: boolean;
  equipment: MembershipSaleEquipment[];
  equipmentState: 'AWAITING_LOCATION' | 'LOADING' | 'READY' | 'ERROR';
  addOns?: MembershipSaleAddOn[];
  customerId?: string | null;
  serviceAddressId?: string | null;
  potentialSavings?: number;
  mode?: 'add' | 'edit';
  initialSelection?: {
    billingOptionId?: string;
    customerChoosesBillingOption?: boolean;
    equipmentIds?: string[];
    deliveredServiceIds?: string[];
  };
  onSelect: (
    option: MembershipBillingOption,
    equipment: MembershipSaleEquipment[],
    addOnIds: string[],
    deliveredServiceIds: string[],
    customerChoosesBillingOption: boolean,
    equipmentSelectionResolved: boolean,
  ) => void;
}

export interface MembershipSaleAddOn {
  id: string;
  name: string;
  description?: string | null;
  type: string;
  basePrice: number;
  estimatedHours?: number | null;
}

const money = (value: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(value);

const equipmentCategoryLabel = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

export function MembershipBillingOptionDialog({
  open,
  onOpenChange,
  plan,
  options,
  services,
  allowDeliveredVisits = false,
  servicesLoading = false,
  servicesError = false,
  equipment,
  equipmentState,
  addOns = [],
  customerId,
  serviceAddressId,
  potentialSavings,
  mode = 'add',
  initialSelection,
  onSelect,
}: Props) {
  const planName = plan?.name ?? '';
  const currency = plan?.priceCurrency ?? 'USD';
  const hasRecordedDeliveredVisits =
    mode === 'edit' && (initialSelection?.deliveredServiceIds?.length ?? 0) > 0;
  const defaultOption = options.find((option) => option.isDefault) ?? options[0];
  const hasUnresolvedBillingOption =
    mode === 'edit' &&
    (!initialSelection?.billingOptionId ||
      !options.some((option) => option.id === initialSelection.billingOptionId));
  const [selectedId, setSelectedId] = useState('');
  const [step, setStep] = useState<'billing' | 'coverage'>('billing');
  const [customerChoosesBillingOption, setCustomerChoosesBillingOption] = useState(false);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<string[]>([]);
  const initializedForOpenRef = useRef(false);
  const knownDeliveredServicesRef = useRef<{
    planId: string | null;
    ids: Set<string>;
  }>({ planId: null, ids: new Set() });
  const planId = plan?.id ?? null;
  if (knownDeliveredServicesRef.current.planId !== planId) {
    knownDeliveredServicesRef.current = { planId, ids: new Set() };
  }
  for (const service of services) {
    knownDeliveredServicesRef.current.ids.add(service.id);
  }
  /**
   * NOT-628 — whether this opening of the dialog has already seeded the covered
   * units, and whether the rep has since chosen for themselves.
   *
   * Seeding used to be gated on `selectedId` alone, so a dialog opened while the
   * home's equipment was still loading seeded an EMPTY selection and the guard
   * then blocked the re-seed when the units arrived. The rep saw the plan priced
   * for a bare home — no extra-unit money — and could save it covering nothing.
   */
  const seededEquipmentRef = useRef(false);
  const equipmentTouchedRef = useRef(false);
  const createEquipment = useCreateEquipment();
  const coverageCategories = useMemo(
    () =>
      Array.from(
        new Set(
          (plan?.coverageRules ?? []).map((rule) => rule.equipmentCategory.trim()).filter(Boolean),
        ),
      ),
    [plan?.coverageRules],
  );
  const [equipmentEditorOpen, setEquipmentEditorOpen] = useState(false);
  const [newEquipmentDescriptionId, setNewEquipmentDescriptionId] = useState('');
  const [newEquipmentDescription, setNewEquipmentDescription] =
    useState<EquipmentDescription | null>(null);
  const [newEquipmentName, setNewEquipmentName] = useState('');
  const [newEquipmentSerial, setNewEquipmentSerial] = useState('');
  const [newEquipment, setNewEquipment] = useState<MembershipSaleEquipment[]>([]);
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<string[]>([]);
  /**
   * The tech is standing in the house: selling the plan and performing a
   * tune-up is one visit, not two. Ticking the service here starts that series
   * at its next occurrence instead of booking work that already happened. Per
   * service — a plan with a spring and a fall visit only spends the one done.
   */
  const [deliveredServiceIds, setDeliveredServiceIds] = useState<string[]>([]);

  const availableEquipment = useMemo(() => {
    const merged = new Map<string, MembershipSaleEquipment>();
    [...equipment, ...newEquipment].forEach((unit) => merged.set(unit.id, unit));
    const coveredCategories = new Set(
      (plan?.coverageRules ?? []).map((rule) => rule.equipmentCategory.trim().toLowerCase()),
    );
    return [...merged.values()].filter((unit) =>
      coveredCategories.has(unit.category.trim().toLowerCase()),
    );
  }, [equipment, newEquipment, plan?.coverageRules]);

  useEffect(() => {
    if (!open) {
      initializedForOpenRef.current = false;
      setStep('billing');
      seededEquipmentRef.current = false;
      equipmentTouchedRef.current = false;
      setEquipmentEditorOpen(false);
      setNewEquipmentDescriptionId('');
      setNewEquipmentDescription(null);
      setNewEquipmentName('');
      setNewEquipmentSerial('');
      setSelectedId((current) => (current ? '' : current));
      setNewEquipment((current) => (current.length > 0 ? [] : current));
      setSelectedEquipmentIds((current) => (current.length > 0 ? [] : current));
      setSelectedAddOnIds((current) => (current.length > 0 ? [] : current));
      setDeliveredServiceIds((current) => (current.length > 0 ? [] : current));
      setCustomerChoosesBillingOption(false);
      return;
    }
    if (!initializedForOpenRef.current) {
      initializedForOpenRef.current = true;
      const savedBillingOptionExists = options.some(
        (option) => option.id === initialSelection?.billingOptionId,
      );
      const initialBillingOptionId = savedBillingOptionExists
        ? initialSelection?.billingOptionId
        : mode === 'edit'
          ? undefined
          : defaultOption?.id;
      setSelectedId(initialBillingOptionId ?? '');
      setCustomerChoosesBillingOption(initialSelection?.customerChoosesBillingOption === true);
      setDeliveredServiceIds(initialSelection?.deliveredServiceIds ?? []);
      if (initialSelection?.equipmentIds) {
        seededEquipmentRef.current = true;
        equipmentTouchedRef.current = true;
        setSelectedEquipmentIds(initialSelection.equipmentIds);
      }
    }
    // Seed once the units are actually known, and never over a choice the rep
    // has already made.
    if (
      seededEquipmentRef.current ||
      equipmentTouchedRef.current ||
      availableEquipment.length === 0
    ) {
      return;
    }
    seededEquipmentRef.current = true;
    setSelectedEquipmentIds(availableEquipment.map((unit) => unit.id));
  }, [availableEquipment, defaultOption?.id, initialSelection, mode, open, options]);

  const selectedEquipment = useMemo(
    () => availableEquipment.filter((unit) => selectedEquipmentIds.includes(unit.id)),
    [availableEquipment, selectedEquipmentIds],
  );
  const unresolvedInitialEquipmentIds = useMemo(() => {
    if (mode !== 'edit') return [];
    const availableIds = new Set(availableEquipment.map((unit) => unit.id));
    return (initialSelection?.equipmentIds ?? []).filter((id) => !availableIds.has(id));
  }, [availableEquipment, initialSelection?.equipmentIds, mode]);
  const hasUnresolvedEquipment =
    mode === 'edit' &&
    (initialSelection?.equipmentIds?.length ?? 0) > 0 &&
    (equipmentState !== 'READY' || unresolvedInitialEquipmentIds.length > 0);
  const hasUnresolvedDeliveredVisits =
    mode === 'edit' &&
    (initialSelection?.deliveredServiceIds ?? []).some(
      (id) => !knownDeliveredServicesRef.current.ids.has(id),
    );
  const editStateCannotBeReconstructed =
    hasUnresolvedBillingOption || hasUnresolvedEquipment || hasUnresolvedDeliveredVisits;

  const pricedOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        basePrice: priceMembershipOptionForEquipment({
          option,
          coverageRules: plan?.coverageRules,
          equipment: selectedEquipment,
        }).priceAmount,
      })),
    [options, plan?.coverageRules, selectedEquipment],
  );

  const selected = useMemo(
    () => pricedOptions.find((option) => option.id === selectedId),
    [pricedOptions, selectedId],
  );
  const selectedSourceOption = useMemo(
    () => options.find((option) => option.id === selectedId),
    [options, selectedId],
  );
  // A free option is the office's pick: it can't be handed to the customer to
  // choose, and it never appears among the options the customer sees.
  const selectedIsFree = isComplimentaryBillingOption(selectedSourceOption);
  const listedOptions = customerChoosesBillingOption
    ? pricedOptions.filter((option) => !isComplimentaryBillingOption(option))
    : pricedOptions;
  /** The add-on price each selected unit past its row's included count carries. */
  const equipmentExtraChargeById = useMemo(() => {
    const charges = new Map<string, MembershipExtraUnitPrice>();

    for (const rule of plan?.coverageRules ?? []) {
      const price = extraUnitPriceOf(rule);
      if (price.extraUnitPrice <= 0) continue;
      const units = selectedEquipment.filter(
        (unit) =>
          unit.category.trim().toLowerCase() === rule.equipmentCategory.trim().toLowerCase(),
      );
      const includedQuantity = Math.max(0, Math.trunc(rule.includedQuantity));
      units.slice(includedQuantity).forEach((unit) => charges.set(unit.id, price));
    }

    return charges;
  }, [plan?.coverageRules, selectedEquipment]);
  const totalExtraEquipmentUnits = equipmentExtraChargeById.size;
  // One pass over every row, rounded the way the server rounds the sale — a
  // sum of per-row amounts can land a cent off the price actually charged.
  const totalExtraEquipmentPrice = useMemo(
    () =>
      selectedSourceOption
        ? priceMembershipOptionForEquipment({
            option: selectedSourceOption,
            coverageRules: plan?.coverageRules,
            equipment: selectedEquipment,
          }).extraPeriodAmount
        : 0,
    [plan?.coverageRules, selectedEquipment, selectedSourceOption],
  );
  const pricedDefault = pricedOptions.find((option) => option.id === defaultOption?.id);
  const defaultAnnual = pricedDefault ? annualizedBillingOptionRevenue(pricedDefault) : 0;

  const hasCoverageStep =
    (plan?.coverageRules?.length ?? 0) > 0 ||
    addOns.length > 0 ||
    (allowDeliveredVisits && services.length > 0);
  const hasBillingStep = pricedOptions.length > 1 || !hasCoverageStep;
  const activeStep = hasBillingStep ? step : 'coverage';
  const hasZeroVisitEquipmentPromise =
    Boolean(plan) &&
    (plan?.coverageRules?.length ?? 0) > 0 &&
    equipmentState === 'READY' &&
    !servicesLoading &&
    !servicesError &&
    plannedMembershipVisits({
      plan: plan!,
      billingOption: selectedSourceOption,
      services,
      equipment: selectedEquipment,
      equipmentState,
    }) <= 0;

  const saveEquipment = () => {
    if (!customerId || !serviceAddressId || !newEquipmentDescription || createEquipment.isPending) {
      return;
    }
    createEquipment.mutate(
      {
        category: newEquipmentDescription.category,
        customerId,
        serviceAddressId,
        equipmentDescriptionId: newEquipmentDescription.id,
        name: newEquipmentName.trim() || newEquipmentDescription.name,
        serialNumber: newEquipmentSerial.trim() || null,
        status: 'ACTIVE',
        disposition: 'EXISTING',
        condition: 'UNKNOWN',
      },
      {
        onSuccess: (created) => {
          const unit = {
            id: created.id,
            category: created.category,
            name: created.name,
            serialNumber: created.serialNumber,
          };
          equipmentTouchedRef.current = true;
          setNewEquipment((current) => [...current.filter((row) => row.id !== unit.id), unit]);
          setSelectedEquipmentIds((current) => Array.from(new Set([...current, unit.id])));
          setNewEquipmentDescriptionId('');
          setNewEquipmentDescription(null);
          setNewEquipmentName('');
          setNewEquipmentSerial('');
          setEquipmentEditorOpen(false);
        },
      },
    );
  };

  if (!open || !plan) return null;

  const commitSelection = () => {
    if (!selected || editStateCannotBeReconstructed) return;
    const committedDeliveredServiceIds = customerChoosesBillingOption
      ? []
      : Array.from(
          new Set([
            ...(mode === 'edit' ? (initialSelection?.deliveredServiceIds ?? []) : []),
            ...deliveredServiceIds.filter((id) => services.some((service) => service.id === id)),
          ]),
        );
    onSelect(
      selected,
      selectedEquipment,
      selectedAddOnIds,
      committedDeliveredServiceIds,
      customerChoosesBillingOption && !selectedIsFree,
      equipmentState === 'READY',
    );
    onOpenChange(false);
  };

  const advance = () => {
    if (activeStep === 'billing' && hasCoverageStep) {
      setStep('coverage');
      return;
    }
    commitSelection();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="grid max-h-[calc(100dvh-2rem)] grid-rows-[auto_auto_minmax(0,1fr)] gap-0 overflow-hidden p-0 sm:max-w-5xl"
      >
        <header className="flex items-start gap-4 border-b px-5 py-4 pr-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-medium tracking-[0.16em] text-muted-foreground uppercase">
                {mode === 'edit' ? 'Edit membership' : 'Add membership'}
              </span>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                {mode === 'edit' ? 'On estimate' : 'Draft'}
              </span>
            </div>
            <DialogTitle className="mt-2 truncate text-xl">{planName}</DialogTitle>
            <DialogDescription className="mt-1">
              {mode === 'edit'
                ? 'Update this membership on the estimate.'
                : 'Configure the membership before adding it to this estimate.'}
            </DialogDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={mode === 'edit' ? `Close ${planName} editor` : `Discard ${planName} draft`}
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </header>

        {hasBillingStep && hasCoverageStep ? (
          <nav className="flex border-b px-5" aria-label="Membership configuration steps">
            <button
              type="button"
              className={`relative flex min-w-0 items-center gap-2 px-1 py-3 text-sm ${
                activeStep === 'billing' ? 'font-medium text-foreground' : 'text-muted-foreground'
              }`}
              onClick={() => setStep('billing')}
            >
              <span className="text-xs tabular-nums text-muted-foreground">01</span>
              <span>Billing</span>
              {selected ? (
                <span className="hidden text-xs text-muted-foreground sm:inline">
                  {money(selected.basePrice, currency)}
                </span>
              ) : null}
              {activeStep === 'billing' ? (
                <span className="absolute right-0 bottom-0 left-0 h-0.5 bg-foreground" />
              ) : null}
            </button>
            <button
              type="button"
              className={`relative ml-8 flex min-w-0 items-center gap-2 px-1 py-3 text-sm ${
                activeStep === 'coverage' ? 'font-medium text-foreground' : 'text-muted-foreground'
              }`}
              onClick={() => setStep('coverage')}
            >
              <span className="text-xs tabular-nums text-muted-foreground">02</span>
              <span>Coverage & visits</span>
              <span className="hidden text-xs text-muted-foreground sm:inline">
                {selectedEquipment.length} {selectedEquipment.length === 1 ? 'unit' : 'units'}
              </span>
              {activeStep === 'coverage' ? (
                <span className="absolute right-0 bottom-0 left-0 h-0.5 bg-foreground" />
              ) : null}
            </button>
          </nav>
        ) : null}

        <div className="grid min-h-0 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_19rem] lg:overflow-hidden">
          <main className="min-w-0 px-5 lg:overflow-y-auto">
            {activeStep === 'billing' ? (
              <div>
                {pricedOptions.length > 1 ? (
                  <section className="border-b py-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-medium">Who chooses?</h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {customerChoosesBillingOption
                            ? 'The customer sees every option; choose which appears first.'
                            : 'The customer sees only the option selected here.'}
                        </p>
                      </div>
                      <div className="inline-flex rounded-lg bg-muted p-1">
                        <button
                          type="button"
                          aria-pressed={!customerChoosesBillingOption}
                          className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors aria-pressed:bg-background aria-pressed:text-foreground aria-pressed:shadow-sm"
                          onClick={() => setCustomerChoosesBillingOption(false)}
                        >
                          Office
                        </button>
                        <button
                          type="button"
                          aria-pressed={customerChoosesBillingOption}
                          aria-label="Let the customer choose the membership billing option"
                          disabled={hasRecordedDeliveredVisits || selectedIsFree}
                          title={
                            selectedIsFree
                              ? 'A free option is picked by the office. The customer never sees it.'
                              : hasRecordedDeliveredVisits
                                ? 'The office must choose after a visit has been added to the estimate.'
                                : undefined
                          }
                          className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors aria-pressed:bg-background aria-pressed:text-foreground aria-pressed:shadow-sm"
                          onClick={() => {
                            setCustomerChoosesBillingOption(true);
                            setDeliveredServiceIds([]);
                          }}
                        >
                          Customer
                        </button>
                      </div>
                    </div>
                  </section>
                ) : null}

                <section className="py-5">
                  <div className="mb-2 flex items-center justify-between gap-3 text-[10px] font-medium tracking-[0.14em] text-muted-foreground uppercase">
                    <span>{customerChoosesBillingOption ? 'Shown first' : 'Billing option'}</span>
                    <span>Price</span>
                  </div>
                  <RadioGroup value={selectedId} onValueChange={setSelectedId} className="gap-0">
                    {listedOptions.map((option) => {
                      const free = isComplimentaryBillingOption(option);
                      const annual = annualizedBillingOptionRevenue(option);
                      const savings = free ? 0 : Math.max(0, defaultAnnual - annual);
                      const checked = selectedId === option.id;
                      return (
                        <label
                          key={option.id}
                          htmlFor={`membership-billing-${option.id}`}
                          className="flex cursor-pointer items-start gap-3 border-t py-4 first:border-t-0"
                        >
                          <RadioGroupItem
                            id={`membership-billing-${option.id}`}
                            value={option.id}
                            className="mt-0.5"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-start justify-between gap-4">
                              <span className="font-medium">
                                {option.label}
                                {free ? (
                                  <span className="ml-2 inline-flex rounded-full bg-muted px-2 py-0.5 align-middle text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
                                    {COMPLIMENTARY_OPTION_LABEL}
                                  </span>
                                ) : null}
                              </span>
                              <span className="shrink-0 font-semibold tabular-nums">
                                {money(option.basePrice, currency)}
                              </span>
                            </span>
                            <span className="mt-1 block text-xs text-muted-foreground">
                              {MEMBERSHIP_BILLING_OPTION_FREQUENCY_LABELS[option.frequency]}
                              {option.termMonths
                                ? ` · ${option.termMonths}-month term`
                                : ' · no fixed term'}
                              {option.frequency !== 'UPFRONT'
                                ? ` · ${money(annual, currency)}/year`
                                : ''}
                            </span>
                            {checked ? (
                              <span className="mt-2 inline-flex rounded-full bg-foreground px-2 py-0.5 text-[10px] font-medium tracking-wide text-background uppercase">
                                {customerChoosesBillingOption ? 'Shown first' : 'Selected'}
                              </span>
                            ) : customerChoosesBillingOption ? (
                              <span className="mt-2 inline-flex rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
                                Also shown
                              </span>
                            ) : null}
                            {savings > 0 ? (
                              <span className="mt-2 block text-xs font-medium text-emerald-700">
                                Saves {money(savings, currency)} per year
                              </span>
                            ) : null}
                          </span>
                        </label>
                      );
                    })}
                  </RadioGroup>
                </section>
              </div>
            ) : (
              <div>
                {(plan.coverageRules?.length ?? 0) > 0 ? (
                  <section className="border-b py-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-medium">Covered equipment</h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Only equipment covered by this plan is available.
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {selectedEquipment.length > 0 ? (
                          <span
                            className={`text-[11px] font-medium tracking-wide uppercase ${
                              totalExtraEquipmentUnits > 0
                                ? 'text-amber-700 dark:text-amber-300'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {selectedEquipment.length} selected
                            {totalExtraEquipmentUnits > 0
                              ? ` · ${totalExtraEquipmentUnits} extra`
                              : ''}
                          </span>
                        ) : null}
                        {customerId && serviceAddressId ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEquipmentEditorOpen((current) => !current)}
                          >
                            <Plus className="mr-1 h-4 w-4" />
                            Equipment not on file
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {plan.coverageRules?.map((rule) => (
                        <span
                          key={rule.id}
                          className="rounded-full bg-muted px-2 py-1 text-[11px] text-muted-foreground"
                        >
                          {equipmentCategoryLabel(rule.equipmentCategory)} · {rule.includedQuantity}{' '}
                          included · +{money(extraUnitPriceOf(rule).extraUnitPrice, currency)}
                          {EXTRA_UNIT_PRICE_UNIT_SUFFIX[extraUnitPriceOf(rule).extraUnitPricePer]}
                        </span>
                      ))}
                    </div>
                    <div className="mt-4 divide-y">
                      {equipmentState === 'LOADING' ? (
                        <p className="py-3 text-sm text-muted-foreground">Loading equipment…</p>
                      ) : equipmentState === 'ERROR' ? (
                        <p className="py-3 text-sm text-destructive">
                          Equipment could not be loaded.
                        </p>
                      ) : equipmentState === 'AWAITING_LOCATION' ? (
                        <p className="py-3 text-sm text-muted-foreground">
                          Select a customer and service address first.
                        </p>
                      ) : availableEquipment.length === 0 ? (
                        <p className="py-3 text-sm text-muted-foreground">
                          No matching equipment is recorded at this address.
                        </p>
                      ) : (
                        availableEquipment.map((unit) => {
                          const checked = selectedEquipmentIds.includes(unit.id);
                          const extraCharge = equipmentExtraChargeById.get(unit.id);
                          return (
                            <label
                              key={unit.id}
                              className="flex cursor-pointer items-center gap-3 py-3"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(next) => {
                                  equipmentTouchedRef.current = true;
                                  setSelectedEquipmentIds((current) =>
                                    next === true
                                      ? Array.from(new Set([...current, unit.id]))
                                      : current.filter((id) => id !== unit.id),
                                  );
                                }}
                                aria-label={`Cover ${unit.name || unit.category}`}
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium">
                                  {unit.name || equipmentCategoryLabel(unit.category)}
                                </span>
                                <span className="block truncate text-xs text-muted-foreground">
                                  {equipmentCategoryLabel(unit.category)}
                                  {unit.serialNumber ? ` · ${unit.serialNumber}` : ''}
                                </span>
                              </span>
                              <span
                                className={
                                  checked && extraCharge
                                    ? 'rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 dark:bg-amber-950/30 dark:text-amber-300'
                                    : 'text-xs text-muted-foreground'
                                }
                              >
                                {!checked
                                  ? 'Not selected'
                                  : extraCharge
                                    ? `+${money(extraCharge.extraUnitPrice, currency)}${
                                        EXTRA_UNIT_PRICE_UNIT_SUFFIX[extraCharge.extraUnitPricePer]
                                      }`
                                    : 'Included'}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                    {equipmentEditorOpen && customerId && serviceAddressId ? (
                      <div className="mt-4 border-t bg-muted/30 px-3 py-4">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="space-y-1.5 sm:col-span-2">
                            <Label>
                              {coverageCategories.length === 1
                                ? `${equipmentCategoryLabel(coverageCategories[0])} type`
                                : 'Equipment type'}
                            </Label>
                            <EquipmentDescriptionCombobox
                              value={newEquipmentDescriptionId}
                              onChange={setNewEquipmentDescriptionId}
                              onSelectDescription={setNewEquipmentDescription}
                              enabled={equipmentEditorOpen}
                              categories={coverageCategories}
                              required
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor="membership-equipment-name">Name</Label>
                            <Input
                              id="membership-equipment-name"
                              value={newEquipmentName}
                              onChange={(event) => setNewEquipmentName(event.target.value)}
                              placeholder={newEquipmentDescription?.name ?? 'e.g. Upstairs unit'}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor="membership-equipment-serial">Serial number</Label>
                            <Input
                              id="membership-equipment-serial"
                              value={newEquipmentSerial}
                              onChange={(event) => setNewEquipmentSerial(event.target.value)}
                              placeholder="Optional"
                            />
                          </div>
                        </div>
                        <div className="mt-3 flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setEquipmentEditorOpen(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            disabled={!newEquipmentDescription || createEquipment.isPending}
                            onClick={saveEquipment}
                          >
                            {createEquipment.isPending ? (
                              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                            ) : (
                              <Plus className="mr-1 h-4 w-4" />
                            )}
                            Add equipment
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </section>
                ) : null}

                {allowDeliveredVisits && services.length > 0 && !customerChoosesBillingOption ? (
                  <section className="border-b py-5">
                    <div className="flex items-baseline justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-medium">Included visits</h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Mark only work already performed on this job.
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {deliveredServiceIds.length} of {services.length} performed
                      </span>
                    </div>
                    <div className="mt-3 divide-y">
                      {services.map((service) => {
                        const delivered = deliveredServiceIds.includes(service.id);
                        const alreadyAddedToEstimate =
                          mode === 'edit' &&
                          initialSelection?.deliveredServiceIds?.includes(service.id) === true;
                        return (
                          <label
                            key={service.id}
                            className={`flex items-center gap-3 py-3 ${
                              alreadyAddedToEstimate ? 'cursor-not-allowed' : 'cursor-pointer'
                            }`}
                          >
                            <Checkbox
                              checked={delivered}
                              disabled={alreadyAddedToEstimate}
                              onCheckedChange={(value) =>
                                setDeliveredServiceIds((current) =>
                                  value === true
                                    ? [...current, service.id]
                                    : current.filter((id) => id !== service.id),
                                )
                              }
                              aria-label={`Count ${service.label ?? 'this included visit'} as already delivered`}
                            />
                            <span className="min-w-0 flex-1 text-sm font-medium">
                              {service.label ?? 'Included visit'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {alreadyAddedToEstimate
                                ? 'Already added to estimate'
                                : delivered
                                  ? 'Performed today'
                                  : 'Not performed'}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                ) : servicesError ? (
                  <p className="border-b py-5 text-sm text-destructive">
                    Included visits could not be loaded.
                  </p>
                ) : servicesLoading ? (
                  <p className="border-b py-5 text-sm text-muted-foreground">
                    Loading included visits…
                  </p>
                ) : null}

                {addOns.length > 0 ? (
                  <section className="py-5">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-sm font-medium">Add-ons</h3>
                      <span className="text-xs text-muted-foreground">
                        {selectedAddOnIds.length} of {addOns.length} selected
                      </span>
                    </div>
                    <div className="mt-3 divide-y">
                      {addOns.map((addOn) => {
                        const checked = selectedAddOnIds.includes(addOn.id);
                        return (
                          <label
                            key={addOn.id}
                            className="flex cursor-pointer items-center gap-3 py-3"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(next) =>
                                setSelectedAddOnIds((current) =>
                                  next === true
                                    ? Array.from(new Set([...current, addOn.id]))
                                    : current.filter((id) => id !== addOn.id),
                                )
                              }
                              aria-label={`Add ${addOn.name}`}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-medium">{addOn.name}</span>
                              {addOn.description ? (
                                <span className="block truncate text-xs text-muted-foreground">
                                  {addOn.description}
                                </span>
                              ) : null}
                            </span>
                            <span className="text-sm font-medium tabular-nums">
                              +{money(addOn.basePrice, currency)}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                ) : null}
              </div>
            )}
          </main>

          <aside
            className="flex min-h-0 flex-col border-t bg-muted/20 lg:border-t-0 lg:border-l"
            aria-live="polite"
          >
            <div className="space-y-5 p-5">
              <div>
                <p className="text-[10px] font-medium tracking-[0.14em] text-muted-foreground uppercase">
                  Member pays
                </p>
                {selected ? (
                  <>
                    <p className="mt-2 text-3xl font-semibold tracking-tight tabular-nums">
                      {money(selected.basePrice, currency)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {MEMBERSHIP_BILLING_OPTION_FREQUENCY_LABELS[selected.frequency]}
                    </p>
                    {Number(plan?.setupFee) > 0 ? (
                      <div className="mt-3 space-y-2 border-t pt-3 text-xs">
                        <p className="flex justify-between gap-3">
                          <span>One-time setup fee</span>
                          <span>+{money(Number(plan?.setupFee), currency)}</span>
                        </p>
                        <p className="flex justify-between gap-3 font-medium">
                          <span>First charge</span>
                          <span>
                            {money(selected.basePrice + Number(plan?.setupFee), currency)}
                          </span>
                        </p>
                      </div>
                    ) : null}
                    {selectedSourceOption && totalExtraEquipmentPrice > 0 ? (
                      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                        Base {money(selectedSourceOption.basePrice, currency)} · equipment +
                        {money(totalExtraEquipmentPrice, currency)}
                      </p>
                    ) : null}
                  </>
                ) : null}
              </div>

              <dl className="space-y-3 border-t pt-4 text-xs">
                <div className="flex gap-3">
                  <dt className="w-20 shrink-0 text-muted-foreground">Billing</dt>
                  <dd className="min-w-0 flex-1 text-right font-medium">
                    {selected?.label ?? 'Not selected'} ·{' '}
                    {customerChoosesBillingOption ? 'customer picks' : 'office picks'}
                  </dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-20 shrink-0 text-muted-foreground">Coverage</dt>
                  <dd className="min-w-0 flex-1 text-right font-medium">
                    {(plan.coverageRules?.length ?? 0) === 0
                      ? 'Not included'
                      : selectedEquipment.length === 0
                        ? 'Recorded later'
                        : `${selectedEquipment.length} ${selectedEquipment.length === 1 ? 'unit' : 'units'}${
                            totalExtraEquipmentUnits > 0 && selectedSourceOption
                              ? ` · ${totalExtraEquipmentUnits} extra +${money(totalExtraEquipmentPrice, currency)} ${membershipBillingPeriodLabel(selectedSourceOption)}`
                              : ''
                          }`}
                  </dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-20 shrink-0 text-muted-foreground">Visits</dt>
                  <dd className="min-w-0 flex-1 text-right font-medium">
                    {services.length} included
                    {deliveredServiceIds.length > 0
                      ? ` · ${deliveredServiceIds.length} performed`
                      : ''}
                  </dd>
                </div>
                {selectedAddOnIds.length > 0 ? (
                  <div className="flex gap-3">
                    <dt className="w-20 shrink-0 text-muted-foreground">Add-ons</dt>
                    <dd className="min-w-0 flex-1 text-right font-medium">
                      {selectedAddOnIds.length} selected
                    </dd>
                  </div>
                ) : null}
              </dl>

              {potentialSavings != null && potentialSavings > 0 ? (
                <div className="flex items-baseline justify-between gap-3 border-t pt-4 text-sm">
                  <span className="text-muted-foreground">Work savings</span>
                  <span className="font-semibold text-emerald-700 tabular-nums">
                    {money(potentialSavings, currency)}
                  </span>
                </div>
              ) : null}

              {hasCoverageStep ? (
                <div className="space-y-2 border-t pt-4 text-xs">
                  <div className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5" />
                    <span className="flex-1">Billing option</span>
                    <span className="text-muted-foreground">Done</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5" />
                    <span className="flex-1">Coverage & visits</span>
                    <span className="text-muted-foreground">Optional</span>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mt-auto border-t p-4">
              {hasUnresolvedBillingOption ? (
                <p role="alert" className="mb-3 text-xs leading-relaxed text-destructive">
                  {initialSelection?.billingOptionId
                    ? "This membership's saved billing option is no longer available."
                    : 'This legacy membership does not record which billing option was sold.'}{' '}
                  Cancel and re-add the membership to change it safely.
                </p>
              ) : null}
              {hasUnresolvedEquipment ? (
                <p role="alert" className="mb-3 text-xs leading-relaxed text-destructive">
                  Some previously covered equipment is no longer available at this address. Restore
                  that equipment or cancel editing to preserve the sold coverage.
                </p>
              ) : null}
              {hasUnresolvedDeliveredVisits ? (
                <p role="alert" className="mb-3 text-xs leading-relaxed text-destructive">
                  Some previously delivered visits are no longer available on this plan. Restore
                  those visits or cancel editing to preserve the sold record.
                </p>
              ) : null}
              {hasZeroVisitEquipmentPromise ? (
                <p role="alert" className="mb-3 text-xs leading-relaxed text-destructive">
                  This equipment-scoped membership has no included visits for the selected units.
                  You can add it to the estimate, but activation will be blocked. Add a visit to the
                  plan, then use Repair included visits on the pending membership.
                </p>
              ) : null}
              <div className="flex gap-2">
                {activeStep === 'coverage' && hasBillingStep && hasCoverageStep ? (
                  <Button type="button" variant="outline" onClick={() => setStep('billing')}>
                    Back
                  </Button>
                ) : null}
                <Button
                  type="button"
                  className="flex-1"
                  disabled={!selected || editStateCannotBeReconstructed}
                  onClick={advance}
                >
                  {activeStep === 'billing' && hasCoverageStep
                    ? 'Next: Coverage & visits'
                    : mode === 'edit'
                      ? 'Save changes'
                      : 'Add to estimate'}
                </Button>
              </div>
              <Button
                type="button"
                variant="ghost"
                className="mt-2 w-full text-muted-foreground"
                onClick={() => onOpenChange(false)}
              >
                {mode === 'edit' ? 'Cancel' : 'Discard draft'}
              </Button>
              <p className="mt-2 text-center text-[11px] text-muted-foreground">
                The estimate still needs Save.
              </p>
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
